import { useScrollLock } from "@/hooks/useScrollLock";
import {
  QuestionCiteItem,
  questionCitelistApi,
} from "@/services/api/questionCitelist";
import { Grid, Modal, Spin } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface CitelistModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  taskId?: string;
  brandId?: string;
  categoryId?: string;
  days?: number;
  metricType?: string;
  modelId?: string;
  onItemClick?: (item: QuestionCiteItem) => void;
}

export const CitelistModal: React.FC<CitelistModalProps> = ({
  open,
  onClose,
  title,
  taskId,
  brandId,
  categoryId,
  days,
  metricType,
  modelId,
  onItemClick,
}) => {
  const { t } = useTranslation();
  const effectiveTitle = title || t("search_detail.insight_related_articles");
  const screens = Grid.useBreakpoint();

  // 防止移动端滚动穿透
  useScrollLock(open);

  const [items, setItems] = useState<QuestionCiteItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [showLoading, setShowLoading] = useState(false); // 延迟显示的loading
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingTimeout, setLoadingTimeout] = useState<number | null>(null);

  // 加载更多
  const loadMore = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadData(nextPage, true);
    }
  };

  // 加载数据
  const loadData = async (pageNum: number = 1, append: boolean = false) => {
    console.log("[CitelistModal] loadData called with taskId:", taskId);
    console.log("[CitelistModal] all params:", {
      taskId,
      brandId,
      categoryId,
      days,
      metricType,
      modelId,
      page: pageNum,
    });

    if (!taskId) {
      console.warn("[CitelistModal] taskId is missing, skipping API call");
      return;
    }

    setLoading(true);

    // 设置2秒后显示loading的定时器
    const timeout = window.setTimeout(() => {
      setShowLoading(true);
    }, 2000);
    setLoadingTimeout(timeout);

    try {
      const response = await questionCitelistApi.getQuestionCiteList({
        taskId,
        brandId,
        categoryId,
        days,
        metricType,
        modelId,
        page: pageNum,
        pageSize: 20,
      });

      // 清除定时器，因为数据已经返回
      if (loadingTimeout !== null) {
        window.clearTimeout(loadingTimeout);
        setLoadingTimeout(null);
      }
      setShowLoading(false);

      if (append) {
        setItems((prev) => [...prev, ...response.list]);
      } else {
        setItems(response.list);
      }

      setHasMore(response.list.length === 20); // 如果返回的数据少于pageSize，说明没有更多了
    } catch (error) {
      // 出错时也要清除定时器
      if (loadingTimeout !== null) {
        window.clearTimeout(loadingTimeout);
        setLoadingTimeout(null);
      }
      setShowLoading(false);
      console.error("加载文章列表失败:", error);
    } finally {
      setLoading(false);
    }
  };

  // 当modal打开时加载数据
  useEffect(() => {
    console.log(
      "[CitelistModal] useEffect triggered, open:",
      open,
      "taskId:",
      taskId
    );
    if (open && taskId) {
      console.log("[CitelistModal] 条件满足，开始加载数据");
      setPage(1);
      setItems([]);
      setShowLoading(false);
      if (loadingTimeout !== null) {
        window.clearTimeout(loadingTimeout);
        setLoadingTimeout(null);
      }
      loadData(1, false);
    }
  }, [open, taskId]);

  // 组件卸载时清除定时器
  useEffect(() => {
    return () => {
      if (loadingTimeout !== null) {
        window.clearTimeout(loadingTimeout);
      }
    };
  }, [loadingTimeout]);

  /*
  const colors = {
    primary: '#21C4B8',
    text: '#0f172a',
    textSecondary: '#475569',
    border: '#e5e7eb',
    background: '#ffffff',
    backgroundSecondary: '#f8fafc'
  }
  */

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="100%"
      closable={false}
      centered
    >
      <div style={{ height: "70vh" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px",
            borderBottom: "1px solid #e8e8e8",
          }}
        >
          <h3 style={{ margin: 0 }}>{effectiveTitle}</h3>
          <button
            onClick={() => {
              console.log("[CitelistModal] 关闭按钮被点击，调用onClose");
              onClose();
            }}
            style={{
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>

        <div style={{ height: "calc(100% - 60px)", overflow: "auto" }}>
          {showLoading && items.length === 0 ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <Spin size="large" />
            </div>
          ) : (
            <>
              {items.map((item, index) => (
                <div
                  key={item.citeIndex}
                  style={{
                    padding: "16px",
                    borderBottom:
                      index < items.length - 1 ? "1px solid #e8e8e8" : "none",
                    cursor: "pointer",
                  }}
                  onClick={() => onItemClick?.(item)}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginBottom: "8px",
                    }}
                  >
                    <img
                      src={item.siteIcon}
                      alt={item.siteName}
                      style={{
                        width: "16px",
                        height: "16px",
                        position: "relative",
                      }}
                      onError={(e) =>
                        ((e.target as HTMLImageElement).style.display = "none")
                      }
                    />
                    <span style={{ fontWeight: 500 }}>{item.siteName}</span>
                    <span style={{ fontSize: "12px", color: "#666" }}>
                      {item.publishTime}
                    </span>
                  </div>
                  <div style={{ fontWeight: 700, marginBottom: "8px" }}>
                    {item.title}
                  </div>
                  <div style={{ fontSize: "13px", color: "#666" }}>
                    {item.snippet}
                  </div>
                </div>
              ))}

              {/* 底部加载指示器 */}
              {loading && items.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "20px",
                  }}
                >
                  <Spin size="small" />
                  <span style={{ marginLeft: "8px", color: "#666" }}>
                    {t("search_detail.competitor_loading")}
                  </span>
                </div>
              )}

              {/* 加载更多按钮 */}
              {hasMore && !loading && (
                <div style={{ padding: "20px", textAlign: "center" }}>
                  <button
                    onClick={loadMore}
                    style={{
                      padding: "8px 24px",
                      backgroundColor: "#21C4B8",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    {t("common.modals.citelist_load_more")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CitelistModal;
